import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

/**
 * RotatingText component that cycles through an array of text values with smooth animations.
 * Uses Framer Motion for better control and React integration.
 *
 * @component
 * @example
 * <RotatingText
 *   texts={["view", "edit", "calculate", "decide"]}
 *   duration={2}
 *   className="text-3xl font-bold text-wisebid-purple"
 * />
 */
interface RotatingTextProps {
  /** Array of text values to cycle through */
  texts: string[];
  /** Duration in seconds for each text to be visible */
  duration?: number;
  /** Additional CSS classes */
  className?: string;
}

export const RotatingText = ({
  texts,
  duration = 2,
  className = ""
}: RotatingTextProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length);
    }, duration * 1000);

    return () => clearInterval(interval);
  }, [texts.length, duration]);

  return (
    <div className={`relative h-8 flex items-center justify-center ${className}`}>
      <AnimatePresence mode="wait">
        <motion.span
          key={currentIndex}
          initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }}
          exit={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }}
          transition={{
            duration: 0.3,
            ease: [0.22, 0.61, 0.36, 1],
            type: "tween"
          }}
          className="absolute w-full text-center"
        >
          {texts[currentIndex]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};
